import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  createRole,
  deleteRole,
  getPermissionsList,
  getRoles,
  getUsers,
  resetUserPassword,
  updateRole,
  updateUserRoles,
} from '../../../api';
import {logError} from '../../../logging/loggingService';
import {emptyRole, mapRoleToState, resolveApiError} from './roleManagementUtils';

export default function useRoleManagementData({
  canReadRoles,
  canManageRoles,
  canManageUsers,
  authUser,
  updateAuthUser,
  persistence,
}) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [roleActionPending, setRoleActionPending] = useState(false);
  const [userActionPending, setUserActionPending] = useState(null);
  const [passwordResetPending, setPasswordResetPending] = useState(null);

  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [isNewRole, setIsNewRole] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resetMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  const loadData = useCallback(async () => {
    if (!canReadRoles) {
      return;
    }
    setLoading(true);
    resetMessages();
    try {
      const [rolesRes, permissionsRes] = await Promise.all([
        getRoles(),
        getPermissionsList(),
      ]);
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      setPermissions(Array.isArray(permissionsRes.data) ? permissionsRes.data : []);

      if (canManageUsers) {
        const usersRes = await getUsers();
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      logError('Failed to load role administration data', err);
      setError(resolveApiError(err, 'Daten konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [canManageUsers, canReadRoles, resetMessages]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!canReadRoles) {
      setEditingRole(null);
      setSelectedRoleId(null);
      return;
    }
    if (isNewRole || roles.length === 0) {
      return;
    }
    const current = selectedRoleId
      ? roles.find((role) => role.id === selectedRoleId)
      : roles[0];
    if (current && current.id !== editingRole?.id) {
      setSelectedRoleId(current.id);
      setEditingRole(mapRoleToState(current));
    }
  }, [canReadRoles, editingRole?.id, isNewRole, roles, selectedRoleId]);

  const handleSelectRole = useCallback((role) => {
    setSelectedRoleId(role.id);
    setEditingRole(mapRoleToState(role));
    setIsNewRole(false);
    resetMessages();
  }, [resetMessages]);

  const startCreateRole = useCallback(() => {
    setSelectedRoleId(null);
    setEditingRole({...emptyRole});
    setIsNewRole(true);
    resetMessages();
  }, [resetMessages]);

  const handleRoleFieldChange = useCallback((field, value) => {
    setEditingRole((prev) => ({
      ...(prev ?? emptyRole),
      [field]: value,
    }));
  }, []);

  const handlePermissionToggle = useCallback((permissionId) => {
    setEditingRole((prev) => {
      if (!prev) {
        return prev;
      }
      const permissionIds = Array.isArray(prev.permissionIds) ? [...prev.permissionIds] : [];
      const index = permissionIds.indexOf(permissionId);
      if (index > -1) {
        permissionIds.splice(index, 1);
      } else {
        permissionIds.push(permissionId);
      }
      return {...prev, permissionIds};
    });
  }, []);

  const handleSaveRole = useCallback(async () => {
    if (!canManageRoles || !editingRole) {
      return;
    }
    setRoleActionPending(true);
    resetMessages();
    try {
      const payload = {
        name: editingRole.name?.trim(),
        description: editingRole.description ?? '',
        permissionIds: editingRole.permissionIds ?? [],
      };
      const result = isNewRole
        ? await createRole(payload)
        : await updateRole(editingRole.id, payload);

      const updatedRole = result?.data;
      if (updatedRole?.id) {
        setSelectedRoleId(updatedRole.id);
        setEditingRole(mapRoleToState(updatedRole));
        setIsNewRole(false);
      }
      setSuccess(isNewRole ? 'Rolle angelegt.' : 'Rolle aktualisiert.');
      await loadData();
    } catch (err) {
      logError('Failed to save role', err);
      setError(resolveApiError(err, 'Rolle konnte nicht gespeichert werden.'));
    } finally {
      setRoleActionPending(false);
    }
  }, [canManageRoles, editingRole, isNewRole, loadData, resetMessages]);

  const handleDeleteRole = useCallback(async () => {
    if (!canManageRoles || !editingRole?.id) {
      return;
    }
    setRoleActionPending(true);
    resetMessages();
    try {
      await deleteRole(editingRole.id);
      setSuccess('Rolle gelöscht.');
      setEditingRole(null);
      setSelectedRoleId(null);
      setIsNewRole(false);
      await loadData();
    } catch (err) {
      logError('Failed to delete role', err);
      setError(resolveApiError(err, 'Rolle konnte nicht gelöscht werden.'));
    } finally {
      setRoleActionPending(false);
    }
  }, [canManageRoles, editingRole?.id, loadData, resetMessages]);

  const handleUserRoleChange = useCallback(async (user, value) => {
    if (!canManageUsers) {
      return;
    }
    const nextRoleIds = Array.isArray(value) ? value : [];
    setUserActionPending(user.id);
    resetMessages();
    try {
      // Wir muessen username und email mitsenden, da diese im UserDTO als @NotBlank markiert sind
      const payload = {
        roleIds: nextRoleIds,
        username: user.username,
        email: user.email,
      };
      const res = await updateUserRoles(user.id, payload);
      const updated = res?.data;
      if (updated) {
        setUsers((prev) => prev.map((entry) => (entry.id === user.id ? updated : entry)));
        if (authUser?.id === updated.id && updateAuthUser) {
          updateAuthUser(updated, {remember: persistence === 'local'});
        }
      }
      setSuccess('Benutzerrollen aktualisiert.');
    } catch (err) {
      logError('Failed to update user roles', err);
      setError(resolveApiError(err, 'Rollen konnten nicht aktualisiert werden.'));
    } finally {
      setUserActionPending(null);
    }
  }, [authUser?.id, canManageUsers, persistence, resetMessages, updateAuthUser]);

  const handleResetPassword = useCallback(async (user) => {
    if (!canManageUsers) {
      return;
    }
    const label = user?.username || user?.email || 'dem Benutzer';
    const confirmed = window.confirm(`Passwort fuer ${label} zuruecksetzen?`);
    if (!confirmed) {
      return;
    }
    setPasswordResetPending(user.id);
    resetMessages();
    try {
      const res = await resetUserPassword(user.id);
      if (res.status >= 200 && res.status < 300) {
        setSuccess('Passwort wurde zurueckgesetzt und per E-Mail versendet.');
      } else {
        setError(typeof res.data === 'string' && res.data ? res.data : 'Passwort konnte nicht zurueckgesetzt werden.');
      }
    } catch (err) {
      logError('Failed to reset user password', err);
      setError(resolveApiError(err, 'Passwort konnte nicht zurueckgesetzt werden.'));
    } finally {
      setPasswordResetPending(null);
    }
  }, [canManageUsers, resetMessages]);

  const roleList = useMemo(() => [...roles].sort((a, b) => a.name.localeCompare(b.name, 'de-DE')), [roles]);
  const permissionList = useMemo(() => [...permissions].sort((a, b) => a.name.localeCompare(b.name, 'de-DE')), [permissions]);

  return {
    roles,
    permissions,
    users,
    loading,
    roleActionPending,
    userActionPending,
    passwordResetPending,
    selectedRoleId,
    editingRole,
    isNewRole,
    error,
    success,
    roleList,
    permissionList,
    handleSelectRole,
    startCreateRole,
    handleRoleFieldChange,
    handlePermissionToggle,
    handleSaveRole,
    handleDeleteRole,
    handleUserRoleChange,
    handleResetPassword,
  };
}
